import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Trash2, Mail, Calendar, Send, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminNotify = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  const fetchEmails = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/notify-emails`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmails(response.data);
    } catch (error) {
      toast.error('Failed to fetch notify list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this email?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/api/notify-emails/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email removed');
      fetchEmails();
    } catch (error) {
      toast.error('Failed to remove email');
    }
  };

  const copyAllEmails = () => {
    const allEmails = emails.map(e => e.email).join(', ');
    navigator.clipboard.writeText(allEmails);
    toast.success('Emails copied to clipboard');
  };

  const openEmailComposer = () => {
    setEmailSubject('');
    setEmailBody('');
    setShowEmailModal(true);
  };

  const sendEmail = () => {
    if (!emailSubject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!emailBody.trim()) {
      toast.error('Please enter a message');
      return;
    }

    const allEmails = emails.map(e => e.email).join(',');
    const subject = encodeURIComponent(emailSubject);
    const body = encodeURIComponent(emailBody);
    
    // Use BCC to hide recipient emails from each other
    const mailtoLink = `mailto:?bcc=${allEmails}&subject=${subject}&body=${body}`;
    
    window.open(mailtoLink, '_blank');
    toast.success('Email client opened with all subscribers!');
    setShowEmailModal(false);
  };

  return (
    <div data-testid="admin-notify">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6]">NOTIFY LIST</h1>
        {emails.length > 0 && (
          <div className="flex gap-3">
            <button
              onClick={copyAllEmails}
              data-testid="copy-emails-btn"
              className="btn-secondary text-sm"
            >
              Copy All Emails
            </button>
            <button
              onClick={openEmailComposer}
              data-testid="send-email-btn"
              className="btn-primary text-sm flex items-center gap-2"
            >
              <Send size={16} />
              Send Email to All
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-[#A19D98]">Loading...</p>
      ) : emails.length === 0 ? (
        <p className="text-[#A19D98]">No subscribers yet.</p>
      ) : (
        <>
          <p className="text-[#A19D98] mb-6">{emails.length} subscribers</p>
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Rally Interest</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((item) => (
                  <tr key={item.id} data-testid={`notify-row-${item.id}`}>
                    <td className="text-[#F2EDE6]">
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="text-[#A19D98]" />
                        <a href={`mailto:${item.email}`} className="hover:text-[#E8651A]">
                          {item.email}
                        </a>
                      </div>
                    </td>
                    <td className="text-[#A19D98]">{item.rally_name || '-'}</td>
                    <td className="text-[#A19D98]">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} />
                        {new Date(item.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 text-[#A19D98] hover:text-[#FF3B30]"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Email Composer Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <div className="bg-[#141414] border border-[#2A2A2A] p-8 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-['Oswald'] text-2xl text-[#F2EDE6]">
                SEND EMAIL TO ALL SUBSCRIBERS
              </h2>
              <button 
                onClick={() => setShowEmailModal(false)} 
                className="text-[#A19D98] hover:text-[#F2EDE6]"
              >
                <X size={24} />
              </button>
            </div>

            <p className="text-[#A19D98] text-sm mb-6">
              This will open your email app with {emails.length} subscribers in BCC (their emails stay private from each other).
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Subject *</label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="e.g., New Rally Announcement - Austin Tour 2026!"
                  data-testid="email-subject-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Message *</label>
                <Textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={8}
                  placeholder="Hey Rally Family!&#10;&#10;We're excited to announce...&#10;&#10;See you on the road!&#10;- Revline Rally Team"
                  data-testid="email-body-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEmailModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={sendEmail}
                  data-testid="send-email-submit"
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  <Send size={16} />
                  Open Email App
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotify;
